import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Calculator, Info, FlaskConical, BarChart3, TrendingUp, DollarSign, Target, Percent, Save, FileText, Loader2, AlertTriangle, FileSpreadsheet, User, Users, Briefcase, Clock, ChevronDown, ChevronUp, Settings, Settings2, Presentation, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { getLEIMeasures, getKPIs } from '../lib/api';
import api from '../lib/api';
import { computeChainLeak } from '../lib/chainLeak';
import ValueTransferContextStrip from '../components/impact/ValueTransferContextStrip';
import { LEICalculatorDrawer } from '../components/LEICalculatorDrawer';
import { AnalysisModeToggle } from '../components/AnalysisModeToggle';
import { SaveScenarioDialog } from '../components/SaveScenarioDialog';
import CohortSelector from '../components/CohortSelector';
import BoardReadyButton from '../components/BoardReadyButton';
import BenchmarkComparison from '../components/BenchmarkComparison';
import NarrativeCard from '../components/analysis/NarrativeCard';
import LeiImpactCard from '../components/LeiImpactCard';
import { MeasureContributionChart, MeasureSharePieChart } from '../components/charts/MeasureContributionChart';
import { MaturitySliderWithGuide } from '../components/MaturitySliderWithGuide';
import { formatCurrency, getCurrencySymbol } from '../lib/utils';
import { ExecutionRiskFactors } from '../components/ExecutionRiskFactors';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

export const ConfigurationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    configuration,
    updateConfiguration,
    updateDampeningFactor,
    setResults,
    leiMeasures,
    setLEIMeasures,
    kpis,
    setKPIs,
    isCalculating,
    setIsCalculating,
    currency,
    setNarrative,
  } = useApp();
  
  const { profileDefaults, hasProfileDefaults, viewAsOrg } = useAuth();

  // Deep-link from Learning Health → Priorities → Risk Areas: preselect the measure.
  const riskDeepLinkApplied = useRef(false);
  useEffect(() => {
    const mid = location.state?.focusMeasureId;
    if (mid && !riskDeepLinkApplied.current) {
      riskDeepLinkApplied.current = true;
      const cfg = { lei_measure_id: mid };
      // Momentum drill-in also carries the KPI so the calculator opens focused on
      // exactly the gap the user clicked (that KPI pre-selected as the only target).
      const kpiId = location.state?.focusKpiId;
      if (kpiId) cfg.kpi_ids = [kpiId];
      updateConfiguration(cfg);
      if (location.state?.focusReason === 'lei-risk-area') {
        toast.info(`Modelling ${location.state.focusMeasureName || 'the flagged measure'} from your Learning Health risk areas.`);
      } else if (location.state?.focusReason === 'momentum-drill') {
        toast.info(`Modelling ${location.state.focusMeasureName || 'this measure'}${location.state.focusKpiLabel ? ` → ${location.state.focusKpiLabel}` : ''} from the Score Trends driver drill-in.`);
      }
    }
  }, [location.state, updateConfiguration]);

  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [analysisMode, setAnalysisMode] = useState('simple'); // 'simple', 'advanced', 'import'
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [localResults, setLocalResults] = useState(null);
  const [resultsStale, setResultsStale] = useState(false);
  const [profileDefaultsApplied, setProfileDefaultsApplied] = useState(false);
  const [isDetailedBreakdownExpanded, setIsDetailedBreakdownExpanded] = useState(false);  // Collapsed by default
  const [isImpactAdjustmentsExpanded, setIsImpactAdjustmentsExpanded] = useState(false);  // Collapsed by default
  const [importedBaselines, setImportedBaselines] = useState(null);
  const [liveBaselines, setLiveBaselines] = useState(null);
  const [isLoadingBaselines, setIsLoadingBaselines] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  // Latest Maturity Assessment for the active org — when present
  // it becomes the source of truth for the maturity slider on
  // first load. The slider value is still editable; user drag
  // overrides this default for the rest of the session.
  const [latestMaturityAssessment, setLatestMaturityAssessment] = useState(null);
  const [maturityAssessmentApplied, setMaturityAssessmentApplied] = useState(false);
  const [maturityOverridden, setMaturityOverridden] = useState(false);
  
  // Track configuration changes to mark results as stale
  const configRef = useRef(null);
  const resultsRef = useRef(null);
  const configSectionRef = useRef(null);  // For scrolling to config section
  
  // Scroll to config section
  const scrollToConfig = () => {
    configSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // Apply profile defaults on mount (only once)
  useEffect(() => {
    if (hasProfileDefaults && !profileDefaultsApplied) {
      updateConfiguration({
        dampening_factors: profileDefaults.dampening_factors,
        current_maturity: profileDefaults.current_maturity,
        headcount: profileDefaults.headcount,
        revenue_per_employee: profileDefaults.revenue_per_employee,
        intervention_cost: profileDefaults.intervention_cost,
        elasticity_scenario: profileDefaults.elasticity_scenario,
      });
      setProfileDefaultsApplied(true);
    }
  }, [hasProfileDefaults, profileDefaults, profileDefaultsApplied, updateConfiguration]);

  // Fetch the latest Maturity Assessment for the active org.
  // When it exists, its overall_score becomes the slider's source
  // of truth — overriding the static profile default. Only runs
  // once per page load and only if the user hasn't manually
  // dragged the slider yet (`maturityOverridden`).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/maturity-assessment/latest');
        if (cancelled) return;
        const a = res?.data?.assessment;
        if (!a || a.overall_score == null) return;
        setLatestMaturityAssessment(a);
        if (!maturityAssessmentApplied && !maturityOverridden && analysisMode !== 'import') {
          updateConfiguration({ current_maturity: Math.round(a.overall_score) });
          setMaturityAssessmentApplied(true);
        }
      } catch (err) {
        // Non-fatal — slider falls back to profile default.
        logger.debug?.('Latest maturity assessment fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [analysisMode, maturityAssessmentApplied, maturityOverridden, updateConfiguration]);
  // Quietly load the org's live LEI baselines so the Advanced calculator can
  // pre-fill each measure's "current state" from imported LMS data (any mode).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/import/data/lei-baselines/latest');
        if (active) setLiveBaselines(res.data?.baselines || null);
      } catch (e) { /* no imported data yet — calculator falls back to manual entry */ }
    })();
    return () => { active = false; };
  }, []);

  
  // Fetch imported baselines when Import mode is selected
  useEffect(() => {
    const fetchImportedBaselines = async () => {
      if (analysisMode === 'import') {
        setIsLoadingBaselines(true);
        try {
          const response = await api.get('/import/data/lei-baselines/latest');
          setImportedBaselines(response.data);
          
          // If we have imported data for the selected measure, use it
          if (response.data.baselines && configuration.lei_measure_id) {
            const baseline = response.data.baselines[configuration.lei_measure_id];
            if (baseline?.calculated_score) {
              // Auto-fill current maturity from imported baseline
              updateConfiguration({ current_maturity: baseline.calculated_score });
            }
          }
        } catch (error) {
          logger.error('Failed to fetch imported baselines:', error);
          toast.error('Failed to load imported data. Please check Data Import.');
        } finally {
          setIsLoadingBaselines(false);
        }
      }
    };
    
    fetchImportedBaselines();
  }, [analysisMode, configuration.lei_measure_id, updateConfiguration]);
  
  // Handle mode change and sync with legacy isAdvancedMode
  const handleModeChange = (newMode) => {
    setAnalysisMode(newMode);
    setIsAdvancedMode(newMode === 'advanced');
    
    // If switching to import mode and we have baseline data for selected measure
    if (newMode === 'import' && importedBaselines?.baselines?.[configuration.lei_measure_id]) {
      const baseline = importedBaselines.baselines[configuration.lei_measure_id];
      if (baseline?.calculated_score) {
        updateConfiguration({ current_maturity: baseline.calculated_score });
        toast.success(`Loaded baseline: ${baseline.calculated_score.toFixed(1)} from ${baseline.measurement_date || 'imported data'}`);
      }
    }
  };
  
  // Mark results as stale when configuration changes after calculation
  useEffect(() => {
    if (localResults) {
      // Create a snapshot of configuration for comparison
      const configSnapshot = JSON.stringify({
        lei_measure_id: configuration.lei_measure_id,
        kpi_ids: configuration.kpi_ids,
        lei_improvement: configuration.lei_improvement,
        elasticity_scenario: configuration.elasticity_scenario,
        dampening_factors: configuration.dampening_factors,
        current_maturity: configuration.current_maturity,
        intervention_cost: configuration.intervention_cost,
        headcount: configuration.headcount,
        manager_support: configuration.manager_support,
        job_alignment: configuration.job_alignment,
        time_to_apply: configuration.time_to_apply,
      });
      
      if (configRef.current !== null && configRef.current !== configSnapshot) {
        setResultsStale(true);
      }
      configRef.current = configSnapshot;
    }
  }, [configuration, localResults]);

  // Get the currently selected measure
  const selectedMeasure = leiMeasures.find(m => m.id === configuration.lei_measure_id);

  // Live Mission Control constraint — powers the Value Transfer
  // context strip and the Expected Value Recovery results tile.
  const [healthDeltas, setHealthDeltas] = useState(null);
  useEffect(() => {
    api.get('/health/quarter-deltas').then(r => setHealthDeltas(r.data)).catch(() => {});
  }, []);
  const chainLeak = computeChainLeak(healthDeltas?.metrics);
  const leakPct = chainLeak ? Math.abs(Math.round(chainLeak.deltaPct)) : null;

  // Toggle KPI selection
  const toggleKpiSelection = (kpiId) => {
    const currentIds = configuration.kpi_ids || [];
    if (currentIds.includes(kpiId)) {
      updateConfiguration({ kpi_ids: currentIds.filter(id => id !== kpiId) });
    } else {
      updateConfiguration({ kpi_ids: [...currentIds, kpiId] });
    }
  };

  // Select/deselect all KPIs
  const toggleAllKpis = (selectAll) => {
    if (selectAll) {
      updateConfiguration({ kpi_ids: kpis.map(k => k.id) });
    } else {
      updateConfiguration({ kpi_ids: [] });
    }
  };

  const handleCalculatorApply = (improvement) => {
    const improvementValue = typeof improvement === 'number' ? improvement : 0;
    updateConfiguration({ lei_improvement: improvementValue });
    toast.success(`Applied ${improvementValue.toFixed(1)}% improvement from calculator`);
  };

  useEffect(() => {
    const loadData = async () => {
      if (leiMeasures.length === 0 || kpis.length === 0) {
        try {
          const [leiRes, kpiRes] = await Promise.all([
            getLEIMeasures(),
            getKPIs(),
          ]);
          setLEIMeasures(leiRes.data);
          setKPIs(kpiRes.data);
        } catch (error) {
          logger.error('Error loading data:', error);
          toast.error('Failed to load reference data');
        }
      }
    };
    loadData();
  }, [leiMeasures.length, kpis.length, setLEIMeasures, setKPIs]);

  const handleCalculate = async () => {
    if (!configuration.kpi_ids || configuration.kpi_ids.length === 0) {
      toast.error('Please select at least one KPI');
      return;
    }
    
    setIsCalculating(true);
    setLocalResults(null);
    
    try {
      // Call the new multi-KPI endpoint with all dampening factors
      const response = await api.post(`/calculate-single-measure`, {
        lei_measure_id: configuration.lei_measure_id,
        kpi_ids: configuration.kpi_ids,
        lei_improvement: configuration.lei_improvement,
        organization_id: viewAsOrg?.id || undefined,
        elasticity_scenario: configuration.elasticity_scenario,
        dampening_factors: configuration.dampening_factors,
        current_maturity: configuration.current_maturity,
        intervention_cost: configuration.intervention_cost,
        headcount: configuration.headcount,
        time_horizon_months: configuration.time_horizon_months || 12,
        // New dampening factors from profile
        scale_dampening_config: profileDefaults?.scale_dampening_config || null,
        industry_type: profileDefaults?.industry_type || 'other',
        learning_modality: profileDefaults?.primary_learning_modality || 'blended',
        time_lag_config: profileDefaults?.time_lag_config || null,
        // Capability Transfer Factors (new)
        manager_support: configuration.manager_support,
        job_alignment: configuration.job_alignment,
        time_to_apply: configuration.time_to_apply,
        learner_population: configuration.learner_population,
      });
      
      setLocalResults(response.data);
      setResults(response.data);
      setResultsStale(false); // Clear stale flag after successful calculation
      // Update config reference to current state
      configRef.current = JSON.stringify({
        lei_measure_id: configuration.lei_measure_id,
        kpi_ids: configuration.kpi_ids,
        lei_improvement: configuration.lei_improvement,
        elasticity_scenario: configuration.elasticity_scenario,
        dampening_factors: configuration.dampening_factors,
        current_maturity: configuration.current_maturity,
        intervention_cost: configuration.intervention_cost,
        headcount: configuration.headcount,
        manager_support: configuration.manager_support,
        job_alignment: configuration.job_alignment,
        time_to_apply: configuration.time_to_apply,
      });
      toast.success('Analysis complete!');
      
      // Scroll to results after a brief delay for render
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      logger.error('Calculation error:', error);
      toast.error('Calculation failed. Please check your inputs.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Group LEI measures by pillar
  const groupedMeasures = leiMeasures.reduce((acc, measure) => {
    if (!acc[measure.pillar]) acc[measure.pillar] = [];
    acc[measure.pillar].push(measure);
    return acc;
  }, {});

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Single Measure ROI"
        description="Set up business cases, dampeners, and target outcomes."
        theme={__pageTheme}
        icon={Settings2}
        testId="single-measure-hero"
        actions={(
          <div className="flex items-center gap-2">
            <AnalysisModeToggle
              mode={analysisMode}
              onModeChange={handleModeChange}
            />
            <Link
              to="/roe-reference#roe-formula"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-xs font-medium backdrop-blur-sm transition-colors"
              data-testid="single-measure-hero-reference-btn"
            >
              <BookOpen className="h-3.5 w-3.5" /> ROE Reference
            </Link>
          </div>
        )}
      />
    )}>
    <TooltipProvider>
      <div className="px-4 md:px-8 lg:px-12 py-6 space-y-8 animate-fade-in">
        {/* Header - matching Portfolio Analysis style */}
        
        {/* Import Mode Banner */}
        {analysisMode === 'import' && (
          <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="import-mode-banner">
            <FileSpreadsheet className="h-5 w-5 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              {isLoadingBaselines ? (
                <p className="text-sm text-teal-800">Loading imported baselines...</p>
              ) : importedBaselines?.count > 0 ? (
                <p className="text-sm text-teal-800">
                  <span className="font-medium">Using imported baselines.</span>{' '}
                  <span className="text-teal-600">
                    {importedBaselines.count} measures available. 
                    {importedBaselines.baselines?.[configuration.lei_measure_id] && (
                      <> Current measure score: <span className="font-semibold">{importedBaselines.baselines[configuration.lei_measure_id].calculated_score?.toFixed(1)}</span></>
                    )}
                  </span>
                </p>
              ) : (
                <p className="text-sm text-amber-800">
                  <span className="font-medium">No imported data found.</span>{' '}
                  <span className="text-amber-600">Go to Data Import to upload your LEI baselines first.</span>
                </p>
              )}
            </div>
            <Link to="/data-management">
              <Button variant="ghost" size="sm" className="text-teal-700 hover:text-teal-900 hover:bg-teal-100">
                Data Import
              </Button>
            </Link>
          </div>
        )}

        {/* Profile Defaults Banner */}
        {hasProfileDefaults && profileDefaultsApplied && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="profile-defaults-banner">
            <User className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Using your profile defaults.</span>{' '}
                <span className="text-blue-600">Dampening factors, maturity, headcount, and other settings are pre-filled from your profile. You can adjust them below for this analysis.</span>
              </p>
            </div>
            <Link to="/profile">
              <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-900 hover:bg-blue-100">
                Edit Profile
              </Button>
            </Link>
          </div>
        )}

        {/* Value transfer context — what is being modelled, where it
            sits in the chain, and any active constraint guidance. */}
        <ValueTransferContextStrip
          measureName={selectedMeasure?.name}
          kpiNames={(configuration.kpi_ids || []).map(id => kpis.find(k => k.id === id)?.name).filter(Boolean)}
          leak={chainLeak}
          leakPct={leakPct}
        />

        {/* Target Cohort Selection */}
        <Card className="border-teal-200 bg-gradient-to-r from-teal-50/50 to-emerald-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg">Target Cohort</CardTitle>
            </div>
            <CardDescription>
              Select a cohort to track this analysis against actual performance data for cohort-specific Gap Analysis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CohortSelector
              selectedCohort={selectedCohort}
              onCohortChange={setSelectedCohort}
              placeholder="Select target cohort (optional)"
              showCreateOption={true}
              className="bg-white"
            />
            {selectedCohort && (
              <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                <Users size={12} />
                Projections will be tracked for: {selectedCohort.name}
              </p>
            )}
          </CardContent>
        </Card>

        <div ref={configSectionRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEI Measure Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">LEI Measure Selection</CardTitle>
              <CardDescription>
                Select the Learning Effectiveness Index measure to analyze
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="lei-measure">LEI Measure</Label>
                <Select
                  value={configuration.lei_measure_id}
                  onValueChange={(value) => updateConfiguration({ lei_measure_id: value })}
                >
                  <SelectTrigger id="lei-measure" data-testid="lei-measure-select">
                    <SelectValue placeholder="Select LEI Measure" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(groupedMeasures).map(([pillar, measures]) => (
                      <React.Fragment key={pillar}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                          {pillar}
                        </div>
                        {measures.map((measure) => (
                          <SelectItem key={measure.id} value={measure.id}>
                            {measure.name}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="lei-improvement">LEI Improvement (%)</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Expected percentage improvement in the selected LEI measure</p>
                    </TooltipContent>
                  </Tooltip>
                  {/* Calculator Button - visible in advanced mode */}
                  {analysisMode === 'advanced' && selectedMeasure?.lei_formula && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => setCalculatorOpen(true)}
                          data-testid="calculator-single-measure"
                        >
                          <FlaskConical className="h-4 w-4 mr-1" />
                          Calculate
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Open LEI Calculator to derive improvement from raw values</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {/* Import indicator - visible in import mode */}
                  {analysisMode === 'import' && importedBaselines?.baselines?.[configuration.lei_measure_id] && (
                    <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded-full font-medium">
                      Baseline: {importedBaselines.baselines[configuration.lei_measure_id].calculated_score?.toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[configuration.lei_improvement]}
                    onValueChange={([value]) => updateConfiguration({ lei_improvement: value })}
                    max={20}
                    min={1}
                    step={0.5}
                    className="flex-1"
                    data-testid="lei-improvement-slider"
                  />
                  <Input
                    id="lei-improvement"
                    type="number"
                    value={configuration.lei_improvement}
                    onChange={(e) => updateConfiguration({ lei_improvement: parseFloat(e.target.value) || 0 })}
                    className="w-20 text-center"
                    data-testid="lei-improvement-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Selection - Multi-select */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Target KPIs</CardTitle>
              <CardDescription>
                Select one or more business KPIs to measure impact against
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Business KPIs</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleAllKpis(true)}
                    className="text-xs h-7"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleAllKpis(false)}
                    className="text-xs h-7"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                {[...kpis].sort((a, b) => a.name.localeCompare(b.name)).map((kpi) => (
                  <div 
                    key={kpi.id} 
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 transition-colors ${
                      (configuration.kpi_ids || []).includes(kpi.id) ? 'bg-teal-50 border border-teal-200' : ''
                    }`}
                    onClick={() => toggleKpiSelection(kpi.id)}
                  >
                    <Checkbox 
                      checked={(configuration.kpi_ids || []).includes(kpi.id)}
                      onCheckedChange={() => toggleKpiSelection(kpi.id)}
                      data-testid={`kpi-checkbox-${kpi.id}`}
                    />
                    <span className="text-sm">{kpi.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {(configuration.kpi_ids || []).length} of {kpis.length} KPIs selected
              </p>

              <div className="space-y-2">
                <Label htmlFor="elasticity-scenario">Elasticity Scenario</Label>
                <Select
                  value={configuration.elasticity_scenario}
                  onValueChange={(value) => updateConfiguration({ elasticity_scenario: value })}
                >
                  <SelectTrigger id="elasticity-scenario" data-testid="elasticity-scenario-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conservative">Conservative (70% of moderate)</SelectItem>
                    <SelectItem value="moderate">Moderate (Base case)</SelectItem>
                    <SelectItem value="aggressive">Aggressive (130% of moderate)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Financial Parameters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Financial Parameters</CardTitle>
              <CardDescription>
                Define the financial context for ROI calculation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="intervention-cost">Intervention Cost ({getCurrencySymbol(currency)})</Label>
                <Input
                  id="intervention-cost"
                  type="number"
                  value={configuration.intervention_cost}
                  onChange={(e) => updateConfiguration({ intervention_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="100000"
                  data-testid="intervention-cost-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="headcount">Headcount</Label>
                  <Input
                    id="headcount"
                    type="number"
                    value={configuration.headcount}
                    onChange={(e) => updateConfiguration({ headcount: parseInt(e.target.value) || 0 })}
                    data-testid="headcount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="revenue-per-employee">Revenue/Employee ({getCurrencySymbol(currency)})</Label>
                  <Input
                    id="revenue-per-employee"
                    type="number"
                    value={configuration.revenue_per_employee}
                    onChange={(e) => updateConfiguration({ revenue_per_employee: parseFloat(e.target.value) || 0 })}
                    data-testid="revenue-per-employee-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time-horizon">Time Horizon (months)</Label>
                <Select
                  value={configuration.time_horizon_months.toString()}
                  onValueChange={(value) => updateConfiguration({ time_horizon_months: parseInt(value) })}
                >
                  <SelectTrigger id="time-horizon" data-testid="time-horizon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="18">18 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                    <SelectItem value="36">36 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Execution Risk Factors */}
          <ExecutionRiskFactors
            values={configuration.dampening_factors}
            onChange={(field, value) => updateDampeningFactor(field, value)}
          />

          {/* Capability Transfer Factors (NEW) */}
          <Card className="lg:col-span-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-violet-50/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Value Transfer Conditions</CardTitle>
                    <CardDescription>
                      Conditions that influence how effectively capability converts into performance. Based on research-backed predictors.
                    </CardDescription>
                  </div>
                </div>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p className="font-medium mb-1">About Value Transfer</p>
                    <p className="text-xs">Research shows 10-60% of learning transfers without support, vs 70-90% with strong systems. These conditions predict transfer success.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Adjustable Factors Hint */}
              <div className="flex items-center justify-between px-3 py-2 bg-purple-100/50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-purple-600" />
                  <span className="text-sm text-purple-700 font-medium">Adjust these factors to match your program</span>
                </div>
                <span className="text-xs text-purple-500 italic">Click dropdowns to customize</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Manager Support */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="manager-support" className="text-sm font-medium">Manager Support</Label>
                    <span className="text-xs text-purple-600 font-medium">(25% weight)</span>
                  </div>
                  <Select
                    value={configuration.manager_support}
                    onValueChange={(value) => updateConfiguration({ manager_support: value })}
                  >
                    <SelectTrigger 
                      id="manager-support" 
                      data-testid="manager-support-select"
                      className="border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex flex-col">
                          <span>None (+0%)</span>
                          <span className="text-xs text-slate-500">Managers unaware</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="aware">
                        <div className="flex flex-col">
                          <span>Aware (+5%)</span>
                          <span className="text-xs text-slate-500">Know but don't act</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="informed">
                        <div className="flex flex-col">
                          <span>Informed (+5%)</span>
                          <span className="text-xs text-slate-500">Basic awareness</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="supportive">
                        <div className="flex flex-col">
                          <span>Supportive (+10%)</span>
                          <span className="text-xs text-slate-500">Encourage application</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="coaching">
                        <div className="flex flex-col">
                          <span>Coaching (+14%)</span>
                          <span className="text-xs text-slate-500">Actively coach & reinforce</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="accountable">
                        <div className="flex flex-col">
                          <span>Accountable (+18%)</span>
                          <span className="text-xs text-slate-500">Measured on team skills</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Job Alignment */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="job-alignment" className="text-sm font-medium">Job Alignment</Label>
                    <span className="text-xs text-purple-600 font-medium">(20% weight)</span>
                  </div>
                  <Select
                    value={configuration.job_alignment}
                    onValueChange={(value) => updateConfiguration({ job_alignment: value })}
                  >
                    <SelectTrigger 
                      id="job-alignment" 
                      data-testid="job-alignment-select"
                      className="border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="generic">
                        <div className="flex flex-col">
                          <span>Generic (+0%)</span>
                          <span className="text-xs text-slate-500">Off-the-shelf content</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="role_relevant">
                        <div className="flex flex-col">
                          <span>Role-Relevant (+5%)</span>
                          <span className="text-xs text-slate-500">Relates to job family</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="job_specific">
                        <div className="flex flex-col">
                          <span>Job-Specific (+10%)</span>
                          <span className="text-xs text-slate-500">Tailored to job tasks</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="performance_integrated">
                        <div className="flex flex-col">
                          <span>Performance-Integrated (+15%)</span>
                          <span className="text-xs text-slate-500">Tied to job workflows</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Time to Apply */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="time-to-apply" className="text-sm font-medium">Time to Apply</Label>
                    <span className="text-xs text-purple-600 font-medium">(20% weight)</span>
                  </div>
                  <Select
                    value={configuration.time_to_apply}
                    onValueChange={(value) => updateConfiguration({ time_to_apply: value })}
                  >
                    <SelectTrigger 
                      id="time-to-apply" 
                      data-testid="time-to-apply-select"
                      className="border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="over_3_months">
                        <div className="flex flex-col">
                          <span>&gt; 3 Months (-5%)</span>
                          <span className="text-xs text-slate-500">Significant forgetting</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="1_3_months">
                        <div className="flex flex-col">
                          <span>1-3 Months (+0%)</span>
                          <span className="text-xs text-slate-500">Some decay expected</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="2_4_weeks">
                        <div className="flex flex-col">
                          <span>2-4 Weeks (+5%)</span>
                          <span className="text-xs text-slate-500">Good retention</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="under_2_weeks">
                        <div className="flex flex-col">
                          <span>&lt; 2 Weeks (+8%)</span>
                          <span className="text-xs text-slate-500">Strong retention</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="immediate">
                        <div className="flex flex-col">
                          <span>Immediate (+10%)</span>
                          <span className="text-xs text-slate-500">Same day/week</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Learner Population - PROMINENT CALLOUT */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-amber-100 border border-amber-200">
                    <Users className="h-6 w-6 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Label htmlFor="learner-population" className="text-base font-semibold text-amber-900">Learner Population</Label>
                      <span className="px-2 py-0.5 text-xs font-bold text-amber-700 bg-amber-200 rounded-full">10% weight</span>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-amber-600" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          <p className="font-medium mb-1">Why this matters</p>
                          <p className="text-xs">Smaller, targeted cohorts typically receive more personalized support, leading to significantly better learning transfer. A focused 500-person program often outperforms a 5,000-person rollout.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                      How many employees will actually participate in this learning initiative?
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Input
                          id="learner-population"
                          type="number"
                          placeholder={configuration.headcount.toLocaleString()}
                          value={configuration.learner_population || ''}
                          onChange={(e) => updateConfiguration({ 
                            learner_population: e.target.value ? parseInt(e.target.value) : null 
                          })}
                          className="w-40 h-12 text-lg font-semibold bg-white border-2 border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                          data-testid="learner-population-input"
                        />
                      </div>
                      <div className="flex-1">
                        {configuration.learner_population ? (
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 bg-amber-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-amber-500 transition-all duration-300" 
                                style={{ width: `${Math.min(100, (configuration.learner_population / configuration.headcount) * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-amber-700 min-w-[80px]">
                              {((configuration.learner_population / configuration.headcount) * 100).toFixed(0)}% of org
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-amber-600 italic">
                            Using full headcount ({configuration.headcount.toLocaleString()} employees)
                          </span>
                        )}
                      </div>
                    </div>
                    {configuration.learner_population && configuration.learner_population < configuration.headcount * 0.3 && (
                      <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-block">
                        ✓ Focused cohort - typically yields better transfer rates
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Auto-calculated factors info */}
              <div className="p-3 bg-purple-100/50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-800">
                  <strong>Auto-calculated from your settings:</strong> L&D Maturity ({configuration.current_maturity}% = +{configuration.current_maturity <= 25 ? 0 : configuration.current_maturity <= 50 ? 3 : configuration.current_maturity <= 75 ? 6 : 8}%) and Learning Modality from profile (15% weight)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Maturity */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Value Realisation Maturity</CardTitle>
              <CardDescription>
                Maturity influences how effectively improvements transfer into outcomes. Diminishing returns apply above 75%.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Current maturity headline — surfaces the actual %
                  and band so the user sees the chosen value at a
                  glance without having to read the slider thumb. */}
              <div className="flex items-baseline gap-3 mb-2" data-testid="maturity-current-headline">
                <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                  {configuration.current_maturity}%
                </span>
                <span className={cn(
                  "text-sm font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full",
                  configuration.current_maturity <= 25 && "bg-rose-100 text-rose-700",
                  configuration.current_maturity > 25 && configuration.current_maturity <= 50 && "bg-amber-100 text-amber-700",
                  configuration.current_maturity > 50 && configuration.current_maturity <= 75 && "bg-sky-100 text-sky-700",
                  configuration.current_maturity > 75 && "bg-emerald-100 text-emerald-700",
                )}>
                  {configuration.current_maturity <= 25 ? 'Emerging'
                    : configuration.current_maturity <= 50 ? 'Developing'
                    : configuration.current_maturity <= 75 ? 'Managed'
                    : 'Optimized'}
                </span>
                <span className="text-xs text-slate-500">value realisation maturity</span>
              </div>
              {/* Source-of-truth indicator. Reads "Synced from
                  Maturity Assessment" when the value came from the
                  most-recent assessment, with a deep-link to view
                  it. Switches to "Manual override" once the user
                  drags the slider so they always know whether the
                  number is grounded in evidence or judgment. */}
              {latestMaturityAssessment && (
                <div
                  className="mb-4 inline-flex items-center gap-2 text-[11px] text-slate-600"
                  data-testid="maturity-source-indicator"
                >
                  {maturityOverridden ? (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold uppercase tracking-wide">
                        Manual override
                      </span>
                      <span>
                        Latest assessment scored {Math.round(latestMaturityAssessment.overall_score)}% ({latestMaturityAssessment.maturity_band || ''})
                        ·{' '}
                        <button
                          type="button"
                          className="text-teal-600 hover:underline font-semibold"
                          onClick={() => {
                            updateConfiguration({ current_maturity: Math.round(latestMaturityAssessment.overall_score) });
                            setMaturityOverridden(false);
                          }}
                          data-testid="maturity-revert-to-assessment"
                        >
                          Revert to assessment
                        </button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">
                        Synced from Maturity Assessment
                      </span>
                      <button
                        type="button"
                        className="text-teal-600 hover:underline font-semibold"
                        onClick={() => navigate('/maturity-health')}
                        data-testid="maturity-view-assessment"
                      >
                        View assessment
                      </button>
                    </>
                  )}
                </div>
              )}
              <MaturitySliderWithGuide
                value={configuration.current_maturity}
                onChange={(value) => {
                  updateConfiguration({ current_maturity: value });
                  setMaturityOverridden(true);
                }}
                showTitle={false}
                testId="maturity-slider"
              />
              {configuration.current_maturity > 75 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Note:</strong> At {configuration.current_maturity}% maturity, diminishing returns will be applied. 
                  The maturity factor will be {(1 - configuration.current_maturity / 100).toFixed(2)}.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calculate Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setLocalResults(null);
              navigate('/');
            }}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCalculate}
            disabled={isCalculating || !configuration.kpi_ids || configuration.kpi_ids.length === 0}
            className="bg-navy-700 hover:bg-navy-800 min-w-[200px]"
            data-testid="run-analysis-btn"
          >
            {isCalculating ? (
              <>
                <div className="spinner mr-2" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="mr-2 h-4 w-4" />
                Run Analysis
              </>
            )}
          </Button>
        </div>

        {/* ========== RESULTS SECTION ========== */}
        {localResults && (
          <div ref={resultsRef} className="space-y-6 mt-8 pt-8 border-t-2 border-slate-200">
            {/* Stale Results Warning */}
            {resultsStale && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="stale-results-warning">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Configuration has changed
                  </p>
                  <p className="text-xs text-amber-600">
                    The results below are from your previous calculation. Click "Recalculate" to update.
                  </p>
                </div>
                <Button 
                  onClick={handleCalculate}
                  disabled={isCalculating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                  data-testid="recalculate-btn"
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Recalculate
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Projected LEI impact */}
            {localResults.lei_impact && (
              <LeiImpactCard leiImpact={localResults.lei_impact} testId="single-lei-impact" />
            )}

            {/* Summary KPI Cards */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-teal-600" />
                <h2 className="text-2xl font-bold text-slate-900">Analysis Results</h2>
              </div>
              <div className="flex gap-2">
                <SaveScenarioDialog 
                  defaultName={`${selectedMeasure?.name || 'Single Measure'} Analysis`}
                  analysisType="single"
                  trigger={
                    <Button variant="outline" data-testid="save-analysis-btn">
                      <Save className="mr-2 h-4 w-4" />
                      Save Scenario
                    </Button>
                  }
                />
                <BoardReadyButton 
                  analysisType="single"
                  defaultName={`${selectedMeasure?.name || 'Single Measure'} Analysis`}
                />
              </div>
            </div>

            <div className={`grid grid-cols-2 gap-4 ${chainLeak ? 'md:grid-cols-3 lg:grid-cols-6' : 'md:grid-cols-5'}`}>
              <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="h-4 w-4" />
                    <p className="text-sm text-teal-100">Total KPI Uplift</p>
                  </div>
                  <p className="text-3xl font-bold">
                    {(localResults.total_kpi_delta_percent || 0).toFixed(2)}%
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-navy-600 to-navy-700 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4" />
                    <p className="text-sm text-slate-200">Financial Impact</p>
                  </div>
                  <p className="text-3xl font-bold">
                    {formatCurrency(localResults.total_financial_impact || 0, currency)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4" />
                    <p className="text-sm text-emerald-100">Net Benefit</p>
                  </div>
                  <p className="text-3xl font-bold">
                    {formatCurrency(localResults.total_net_benefit || 0, currency)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4" />
                    <p className="text-sm text-purple-100">ROI</p>
                  </div>
                  <p className="text-3xl font-bold">
                    {((localResults.total_roi_percent || 0) / 100).toFixed(1)}x
                  </p>
                </CardContent>
              </Card>
              {chainLeak && (
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="smr-evr-tile">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4" />
                      <p className="text-sm text-orange-100">Expected Value Recovery</p>
                    </div>
                    <p className="text-3xl font-bold">
                      +{Math.min(localResults.total_kpi_delta_percent || 0, leakPct).toFixed(1)}%
                    </p>
                    <p className="text-xs text-orange-100 mt-1">of {chainLeak.stage.label} {leakPct}% leak</p>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4" />
                    <p className="text-sm text-amber-100">Payback Period</p>
                  </div>
                  <p className="text-3xl font-bold">
                    {localResults.payback_months != null 
                      ? `${localResults.payback_months.toFixed(1)} mo`
                      : 'N/A'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Analysis Notes - Detailed Warnings */}
            {localResults.warning_details && localResults.warning_details.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Analysis Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {localResults.warning_details.map((detail, idx) => (
                    <div key={`warn-${detail.type}-${idx}`} className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <p className="font-semibold text-amber-800">
                          {localResults.warnings?.[idx] || detail.type}
                        </p>
                        <p className="text-sm text-amber-700">{detail.explanation}</p>
                        {detail.typical_ranges && (
                          <div className="text-xs bg-white/50 rounded p-2 mt-1">
                            <p className="font-medium text-amber-800 mb-1 underline">Typical ROI Ranges:</p>
                            <div className="space-y-0.5">
                              {Object.entries(detail.typical_ranges).map(([key, val]) => (
                                <div key={key} className="flex">
                                  <span className="capitalize text-amber-600 w-24">{key}:</span>
                                  <span className="font-mono text-amber-700">{val}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {detail.guidance && detail.guidance.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-amber-800 mb-1">Recommended Actions:</p>
                            <ul className="space-y-1">
                              {detail.guidance.map((g, gIdx) => (
                                <li key={`${detail.type || 'g'}-${gIdx}-${String(g).slice(0,24)}`} className="text-xs text-amber-700 flex items-start gap-2">
                                  <span className="text-amber-500">→</span>
                                  {g}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={scrollToConfig}
                        className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        <Settings className="mr-1 h-4 w-4" />
                        Adjust Parameters
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* How We Got Here Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                <BarChart3 className="h-5 w-5 text-navy-700" />
                <h2 className="text-xl font-bold text-slate-900">How We Got Here</h2>
                <span className="text-sm text-slate-500 ml-2">Impact breakdown by KPI</span>
              </div>

              {/* Transform kpi_results for charts - using KPIs as the "measures" for this view */}
              <MeasureContributionChart
                measureResults={localResults.kpi_results?.map(k => ({
                  lei_measure_id: k.kpi_id,
                  lei_measure_name: k.kpi_name,
                  pillar: 'KPI',
                  lei_improvement: configuration.lei_improvement,
                  kpi_delta_percent: k.kpi_delta_percent || 0,
                  financial_impact: k.financial_impact || 0,
                  weight: k.weight || 0.7,
                })) || []}
                totalKpiDelta={localResults.total_kpi_delta_percent || 0}
                totalFinancialImpact={localResults.total_financial_impact || 0}
              />

              {/* Pie Chart and Calculation Methodology */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <MeasureSharePieChart
                  measureResults={localResults.kpi_results?.map(k => ({
                    lei_measure_id: k.kpi_id,
                    lei_measure_name: k.kpi_name,
                    financial_impact: k.financial_impact || 0,
                  })) || []}
                  totalFinancialImpact={localResults.total_financial_impact || 0}
                  title="Financial Impact Share by KPI"
                />

                {/* Calculation Notes Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading">Calculation Methodology</CardTitle>
                    <CardDescription className="text-xs">
                      Key factors applied to this analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      {localResults.calculation_notes?.map((note, idx) => (
                        <div key={`note-${idx}-${note.slice(0, 20)}`} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                          <span className="text-slate-600">{note}</span>
                        </div>
                      ))}
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-700">Formula Applied:</p>
                        <p className="text-xs font-mono text-slate-600 mt-1">
                          KPI Delta = LEI% × Weight × Elasticity × Dampening × Maturity
                        </p>
                        <p className="text-xs font-mono text-slate-600 mt-1">
                          Financial Impact = KPI Delta × Baseline × <span className="text-amber-600 font-semibold">Scale Factor</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Impact Adjustments Card - Shows all dampening factors - Collapsible */}
              {localResults && (
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <Collapsible open={isImpactAdjustmentsExpanded} onOpenChange={setIsImpactAdjustmentsExpanded}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="pb-2 cursor-pointer hover:bg-amber-100/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                              <Target className="h-4 w-4 text-amber-600" />
                              Impact Adjustments Applied
                              {isImpactAdjustmentsExpanded ? (
                                <ChevronUp className="h-4 w-4 text-amber-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-amber-400" />
                              )}
                            </CardTitle>
                            {!isImpactAdjustmentsExpanded && (
                              <CardDescription className="text-sm text-muted-foreground">
                                Click to view factors that adjust theoretical impact
                              </CardDescription>
                            )}
                          </div>
                          {localResults.combined_adjustment_factor && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 text-amber-700">
                              <div className="text-right">
                                <p className="text-lg font-bold">{(localResults.combined_adjustment_factor * 100).toFixed(1)}%</p>
                                <p className="text-xs font-medium">Combined Adjustment</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Combined Adjustment Factor */}
                          {localResults.combined_adjustment_factor && (
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-300">
                              <div>
                                <p className="text-sm font-medium text-slate-700">Combined Adjustment</p>
                                <p className="text-xs text-slate-500">All factors multiplied</p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-amber-600">
                                  {(localResults.combined_adjustment_factor * 100).toFixed(1)}%
                                </p>
                                <p className="text-xs text-slate-500">of theoretical impact</p>
                              </div>
                            </div>
                          )}

                          {/* Individual Factors Grid */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Scale Dampening */}
                            {localResults.scale_dampening && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Scale Dampening</p>
                                  <span className="text-sm font-bold text-amber-600">
                                    {(localResults.scale_dampening.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{localResults.scale_dampening.bracket}</p>
                              </div>
                            )}

                            {/* Industry Modifier */}
                            {localResults.industry_modifier && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Industry</p>
                                  <span className="text-sm font-bold text-blue-600">
                                    {(localResults.industry_modifier.modifier * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{localResults.industry_modifier.industry}</p>
                              </div>
                            )}

                            {/* Learning Modality */}
                            {localResults.learning_modality && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Learning Modality</p>
                                  <span className="text-sm font-bold text-purple-600">
                                    {(localResults.learning_modality.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{localResults.learning_modality.modality}</p>
                              </div>
                            )}

                            {/* Time Lag */}
                            {localResults.time_lag && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Time Lag</p>
                                  <span className="text-sm font-bold text-teal-600">
                                    {(localResults.time_lag.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {localResults.time_lag.effective_months} effective months
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Capability Transfer Rate Card (NEW) */}
                          {localResults.transfer_rate && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Users className="h-5 w-5 text-purple-600" />
                                  <div>
                                    <p className="text-sm font-medium text-purple-800">Capability Transfer Rate</p>
                                    <p className="text-xs text-purple-600">How much learning transfers to job performance</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-purple-700">
                                    {localResults.transfer_rate.transfer_rate_percent}%
                                  </p>
                                  <p className="text-xs text-purple-500">
                                    Base {localResults.transfer_rate.base_rate}% {localResults.transfer_rate.total_adjustment >= 0 ? '+' : ''}{localResults.transfer_rate.total_adjustment}%
                                  </p>
                                </div>
                              </div>
                              
                              {/* Factor Breakdown */}
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                {localResults.transfer_rate.factors && Object.entries(localResults.transfer_rate.factors).map(([key, factor]) => (
                                  <div key={key} className="p-2 bg-white border border-slate-200 rounded border border-purple-100">
                                    <div className="flex justify-between items-center">
                                      <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                      <span className={`font-medium ${factor.points >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                        {factor.points >= 0 ? '+' : ''}{factor.points}%
                                      </span>
                                    </div>
                                    <p className="text-slate-400 text-[10px] mt-0.5 truncate" title={factor.description}>
                                      {factor.description}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Raw vs Adjusted Comparison */}
                          {localResults.total_financial_impact_raw && localResults.combined_adjustment_factor < 1 && (
                            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-amber-200">
                              <div className="p-2 bg-white rounded border border-slate-200">
                                <p className="text-xs text-slate-500">Theoretical Impact</p>
                                <p className="text-sm font-mono font-semibold text-slate-400 line-through">
                                  {formatCurrency(localResults.total_financial_impact_raw, currency)}
                                </p>
                              </div>
                              <div className="p-2 bg-white rounded border border-amber-300">
                                <p className="text-xs text-amber-600">Realistic Impact</p>
                                <p className="text-sm font-mono font-semibold text-amber-700">
                                  {formatCurrency(localResults.total_financial_impact, currency)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Factor Details Accordion */}
                          <div className="text-xs text-slate-500 pt-2 border-t border-amber-100 space-y-2">
                            {localResults.industry_modifier && (
                              <p><strong>Industry:</strong> {localResults.industry_modifier.rationale}</p>
                            )}
                            {localResults.learning_modality && (
                              <p><strong>Modality:</strong> {localResults.learning_modality.description}</p>
                            )}
                            {localResults.time_lag && (
                              <p><strong>Time Lag:</strong> {localResults.time_lag.explanation}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              )}

              {/* Detailed Breakdown Table - Collapsible */}
              <Card>
                <Collapsible open={isDetailedBreakdownExpanded} onOpenChange={setIsDetailedBreakdownExpanded}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-slate-600" />
                            Detailed Breakdown by KPI
                            {isDetailedBreakdownExpanded ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </CardTitle>
                          {!isDetailedBreakdownExpanded && (
                            <CardDescription className="text-sm text-muted-foreground">
                              Click to view impact of {selectedMeasure?.name || 'selected measure'} on each KPI
                            </CardDescription>
                          )}
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700">
                          <div className="text-right">
                            <p className="text-xs font-medium">{localResults.kpi_results?.length || 0} KPIs</p>
                            <p className="text-lg font-bold">{formatCurrency(localResults.total_financial_impact || 0, currency)}</p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 font-semibold text-slate-600">KPI</th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">Weight</th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">Elasticity</th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">KPI Uplift</th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">Financial Impact</th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">% of Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {localResults.kpi_results?.map((kr, idx) => (
                              <tr key={kr.kpi_id} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                                <td className="py-3 px-4 font-medium text-slate-900">{kr.kpi_name}</td>
                                <td className="py-3 px-4 text-right font-mono text-slate-600">
                                  {(kr.weight || 0).toFixed(2)}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-slate-600">
                                  {(kr.elasticity || 0).toFixed(2)}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-teal-600">
                                  {(kr.kpi_delta_percent || 0).toFixed(3)}%
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-teal-600 font-semibold">
                                  {formatCurrency(kr.financial_impact || 0, currency)}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-navy-600">
                                  {localResults.total_financial_impact > 0 
                                    ? ((kr.financial_impact / localResults.total_financial_impact) * 100).toFixed(1)
                                    : 0}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-slate-300 bg-slate-100">
                              <td className="py-3 px-4 font-bold text-slate-900" colSpan="3">Total</td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-teal-700">
                                {(localResults.total_kpi_delta_percent || 0).toFixed(3)}%
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-teal-700">
                                {formatCurrency(localResults.total_financial_impact || 0, currency)}
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-navy-700">100%</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            </div>

            {/* Tell Me What This Means - AI Narrative */}
            <NarrativeCard
              results={localResults}
              configuration={configuration}
              analysisType="single"
              measureName={selectedMeasure?.name || 'Selected Measure'}
              kpiNames={configuration.kpi_ids?.map(id => kpis.find(k => k.id === id)?.name).filter(Boolean).join(', ') || 'Selected KPIs'}
              currency={currency}
              onNarrativeGenerated={setNarrative}
            />

            {/* Industry Benchmark Comparison */}
            <BenchmarkComparison 
              results={{
                roi_percent: localResults.total_roi_percent,
                kpi_delta_percent: localResults.total_kpi_delta_percent,
                payback_months: localResults.payback_months,
                financial_impact: localResults.total_financial_impact,
              }}
              configuration={configuration}
              measures={leiMeasures}
            />
          </div>
        )}

        {/* LEI Calculator Drawer */}
        <LEICalculatorDrawer
          open={calculatorOpen}
          onOpenChange={setCalculatorOpen}
          measure={selectedMeasure}
          currentImprovement={configuration.lei_improvement}
          liveCurrentScore={liveBaselines?.[configuration.lei_measure_id]?.calculated_score ?? null}
          onApply={handleCalculatorApply}
        />
      </div>
    </TooltipProvider>
    </PageShell>
  );
};

export default ConfigurationPage;
